import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertController, ModalController } from '@ionic/angular';
import { DataService } from '../../data.service';
import moment from 'moment';
import { ZaradekLefedettsegComponent } from './zaradek-lefedettseg.component';

@Component({
  selector: 'app-zaradek-modal',
  templateUrl: './zaradek-modal.component.html',
})
export class ZaradekModalComponent implements OnInit {
  @Input() oktazon: string;
  @Input() tanuloNev: string;
  @Input() intezmenyId: string;

  zaradekok: any[] = [];
  betoltes = false;
  mentes = false;

  szerkesztettId: string | null = null;
  urlap = { datum: moment().format('YYYY-MM-DD'), szoveg: '', megjelenes_helye: 'torzslap', kategoria: '' };

  // A megjelenés helye – jelenleg csak törzslap, de bővíthető.
  megjelenesOpciok = [{ value: 'torzslap', label: 'Törzslap' }];

  // Záradék-törzslista (sablonok) és kategória-feliratok. A 20/2012. EMMI r. 1. melléklete
  // szerinti sablonok kóddal (melleklet_kod) jönnek, és a backend elöl adja őket.
  sablonok: any[] = [];
  valasztottSablonId = '';
  kategoriaLabel: { [k: string]: string } = {
    tovabbhaladas: 'Nevelőtestületi határozat (továbbhaladás)',
    jogviszony: 'Jogviszony',
    mentesites: 'Mentesítés / vizsga',
    egyeb: 'Egyéb',
    kollegium: 'Kollégium',
  };
  // A melléklet dokumentum-jelölései (Bn., N., TI., B., …) olvasható alakban.
  dokumentumLabel: { [k: string]: string } = {
    beirasi_naplo: 'Beírási napló',
    naplo: 'Napló',
    torzslap: 'Törzslap',
    bizonyitvany: 'Bizonyítvány',
    pot_torzslap: 'Póttörzslap',
    pot_bizonyitvany: 'Pótbizonyítvány',
    kollegiumi_torzskonyv: 'Kollégiumi törzskönyv',
    kollegiumi_naplo: 'Kollégiumi napló',
    csoportnaplo: 'Csoportnapló',
  };

  // A törölt záradékok (logikai törlés) és az előzmények – csak munkatársnak.
  toroltekIs = false;
  elozmeny: { [id: string]: any[] | null } = {};
  elozmenyNyitva: { [id: string]: boolean } = {};

  constructor(
    private http: HttpClient,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private data: DataService
  ) {}

  ngOnInit() {
    this.betolt();
    this.betoltSablonok();
  }

  private base() {
    return `/api/zaradek/${this.oktazon}`;
  }

  megjelenesLabel(value: string): string {
    return this.megjelenesOpciok.find((o) => o.value === value)?.label || value || '-';
  }

  kategoriaNev(value: string): string {
    return value ? (this.kategoriaLabel[value] || value) : '';
  }

  dokumentumokNev(lista: string): string {
    return String(lista || '').split(',').map((x) => x.trim()).filter(Boolean)
      .map((x) => this.dokumentumLabel[x] || x).join(', ');
  }

  // A sablonválasztó sora: „[28. a)] [Jogviszony] szöveg”.
  sablonFelirat(s: any): string {
    return `${s.melleklet_kod ? `[${s.melleklet_kod}] ` : ''}[${this.kategoriaNev(s.kategoria)}] ${s.szoveg}`;
  }

  get valasztottSablon(): any {
    return this.sablonok.find((x) => x.id === this.valasztottSablonId) || null;
  }

  muveletNev(m: string): string {
    return ({ letrehozas: 'Létrehozás', modositas: 'Módosítás', torles: 'Törlés' } as any)[m] || m;
  }

  // Az előtte–utána képből a megváltozott mezők (a naplóban ezt mutatjuk).
  valtozasok(n: any): { mezo: string; elotte: any; utana: any }[] {
    const nevek: { [k: string]: string } = {
      datum: 'Dátum', szoveg: 'Szöveg', megjelenes_helye: 'Megjelenés helye',
      kategoria: 'Kategória', melleklet_kod: 'Mellékleti kód',
    };
    const e = n?.elotte || {}, u = n?.utana || {};
    if (n?.muvelet !== 'modositas') return [];
    return Object.keys(nevek)
      .filter((k) => (e[k] ?? null) !== (u[k] ?? null))
      .map((k) => ({ mezo: nevek[k], elotte: e[k] ?? '—', utana: u[k] ?? '—' }));
  }

  elozmenyValt(z: any) {
    this.elozmenyNyitva[z.id] = !this.elozmenyNyitva[z.id];
    if (!this.elozmenyNyitva[z.id]) return;
    this.elozmeny[z.id] = null;
    this.http.get<any[]>(`${this.base()}/${z.id}/naplo`).subscribe({
      // A változott mezőket egyszer számoljuk ki (a sablon ne kapjon minden körben új tömböt).
      next: (rows) => (this.elozmeny[z.id] = (Array.isArray(rows) ? rows : []).map((n) => ({ ...n, valt: this.valtozasok(n) }))),
      error: (e: any) => {
        this.elozmeny[z.id] = [];
        this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az előzményt.', 'error');
      },
    });
  }

  toroltekValt() {
    this.toroltekIs = !this.toroltekIs;
    this.betolt();
  }

  get aktivDb(): number {
    return this.zaradekok.filter((z) => !Number(z.torolve)).length;
  }

  betoltSablonok() {
    this.http.get<any[]>('/api/zaradek/sablonok/lista').subscribe({
      next: (rows) => (this.sablonok = Array.isArray(rows) ? rows : []),
      error: () => (this.sablonok = []),
    });
  }

  // Sablon kiválasztásakor a szöveg és a kategória kitöltődik (a szöveg szerkeszthető marad).
  sablonValaszt(id: string) {
    this.valasztottSablonId = id;
    const s = this.sablonok.find((x) => x.id === id);
    if (s) {
      this.urlap.szoveg = s.szoveg || '';
      this.urlap.kategoria = s.kategoria || '';
    }
  }

  betolt() {
    this.betoltes = true;
    this.http
      .get<any[]>(`${this.base()}?intezmeny=${encodeURIComponent(this.intezmenyId || '')}${this.toroltekIs ? '&torolt=1' : ''}`)
      .subscribe({
        next: (rows) => {
          this.zaradekok = Array.isArray(rows) ? rows : [];
          this.betoltes = false;
        },
        error: (e: any) => {
          this.betoltes = false;
          this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a záradékokat.', 'error');
        },
      });
  }

  ujUrlap() {
    this.szerkesztettId = null;
    this.valasztottSablonId = '';
    this.urlap = { datum: moment().format('YYYY-MM-DD'), szoveg: '', megjelenes_helye: 'torzslap', kategoria: '' };
  }

  szerkeszt(z: any) {
    if (Number(z.torolve)) return;
    this.szerkesztettId = z.id;
    this.valasztottSablonId = '';
    this.urlap = {
      datum: z.datum || moment().format('YYYY-MM-DD'),
      szoveg: z.szoveg || '',
      megjelenes_helye: z.megjelenes_helye || 'torzslap',
      kategoria: z.kategoria || '',
    };
  }

  ment() {
    const szoveg = (this.urlap.szoveg || '').trim();
    if (!szoveg) {
      this.data.sendToast('A záradék szövege kötelező.', 'error');
      return;
    }
    this.mentes = true;
    const body = {
      intezmeny: this.intezmenyId,
      datum: this.urlap.datum,
      szoveg,
      megjelenes_helye: this.urlap.megjelenes_helye,
      kategoria: this.urlap.kategoria || null,
      // A mellékleti kódot a szerver a sablonból veszi; a beírót a bejelentkezésből.
      sablon_id: this.valasztottSablonId || null,
    };
    const req = this.szerkesztettId
      ? this.http.put(`${this.base()}/${this.szerkesztettId}`, body)
      : this.http.post(this.base(), body);
    req.subscribe({
      next: () => {
        this.mentes = false;
        this.data.sendToast(this.szerkesztettId ? 'Záradék módosítva.' : 'Záradék felvéve.', 'success');
        this.ujUrlap();
        this.betolt();
      },
      error: (e: any) => {
        this.mentes = false;
        this.data.sendToast(e?.error?.error || 'Mentési hiba.', 'error');
      },
    });
  }

  // Logikai törlés: a záradék nem tűnik el, csak minden dokumentumból kimarad; az ok kötelező.
  async torol(z: any) {
    const alert = await this.alertCtrl.create({
      header: 'Záradék törlése',
      message: 'A záradék a bizonyítványból, a törzslapról és a nyomtatványokból kimarad, de az előzményben megmarad. Add meg a törlés okát.',
      inputs: [{ name: 'ok', type: 'textarea', placeholder: 'A törlés oka (kötelező)…' }],
      buttons: [
        { text: 'Mégse', role: 'cancel' },
        {
          text: 'Törlés',
          role: 'destructive',
          handler: (adat: any) => {
            const ok = String(adat?.ok || '').trim();
            if (!ok) {
              this.data.sendToast('A törlés oka kötelező.', 'error');
              return false;
            }
            this.http.delete(`${this.base()}/${z.id}`, { body: { ok, intezmeny: this.intezmenyId } }).subscribe({
              next: () => {
                this.data.sendToast('Záradék törölve.', 'success');
                if (this.szerkesztettId === z.id) this.ujUrlap();
                delete this.elozmeny[z.id];
                delete this.elozmenyNyitva[z.id];
                this.betolt();
              },
              error: (e: any) => this.data.sendToast(e?.error?.error || 'Törlési hiba.', 'error'),
            });
            return true;
          },
        },
      ],
    });
    await alert.present();
  }

  /** A záradék-sablonok összevetése a 20/2012. EMMI r. 1. mellékletével – külön ablakban, e fölött. */
  async lefedettseg() {
    const m = await this.modalCtrl.create({
      component: ZaradekLefedettsegComponent,
      componentProps: { intezmenyId: this.intezmenyId || '' },
    });
    await m.present();
  }

  bezar() {
    this.modalCtrl.dismiss();
  }
}
